import { PolicyBalance } from '@/common/Policy';
import axios, { AxiosResponse } from 'axios';

const BillPay = {
	/**
	 * Fetches the latest and greatest `PolicyBalance` for a policy without any transformations.
	 */
	async getRawBalance(policyNumber: string): Promise<AxiosResponse<PolicyBalance>> {
		const response: AxiosResponse<PolicyBalance> = await axios({
			baseURL: process.env.VUE_APP_API_URI,
			method: 'GET',
			url: `/balance?policy_no=${policyNumber}`,
		});
		return response;
	},

	/**
	 * Clones a `PolicyBalance` object and enforces a minimum value of 0 (dollars) to the clone's
	 * `account` and `due` fields.
	 */
	nonnegativeBalance(balance: PolicyBalance): PolicyBalance {
		return {
			...balance,
			account: Math.max(balance.account ?? 0, 0),
			due: Math.max(balance.due ?? 0, 0),
		};
	},

	/**
	 * Fetches the latest and greatest `PolicyBalance` for a policy and transforms the value via
	 * `nonnegativeBalance()`.
	 */
	async getBalance(policyNumber: string): Promise<AxiosResponse<PolicyBalance>> {
		const response: AxiosResponse<PolicyBalance> = await BillPay.getRawBalance(policyNumber);
		response.data = BillPay.nonnegativeBalance(response.data);
		return response;
	},

	async getBalanceData(productId: string): Promise<PolicyBalance> {
		const response: AxiosResponse<PolicyBalance> = await axios({
			baseURL: process.env.VUE_APP_API_URI4,
			method: 'GET',
			url: `/billpay/balance?product_id=${productId}`,
		});
		response.data = BillPay.nonnegativeBalance(response.data);
		return response.data;
	},

	getPaymentSources: async () => {
		const getPaymentSources = await axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI4 + '/billpay/sources',
		});

		return getPaymentSources.data.map(source => {
			if (source.type === 'card') {
				source.verified = true;
			}

			if (source.type === 'bank_account' && source.verified === undefined) {
				source.verified = false;
			}

			return source;
		});
	},
	getPaymentMethods: async () => {
		return await axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI + '/stripe/source',
		});
	},
	addPaymentMethod: async data => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI + '/stripe/source',
			data,
		});
	},
	verifyBankAccount: async data => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI + '/stripe/source/verify',
			data,
		});
	},
	submitPayment: async data => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI + '/stripe/charge',
			data,
		});
	},
	editPaymentMethod: async data => {
		return await axios({
			method: 'PUT',
			url: process.env.VUE_APP_API_URI + '/stripe/source',
			data,
		});
	},
	deletePaymentMethod: async data => {
		return await axios({
			method: 'DELETE',
			url: process.env.VUE_APP_API_URI + '/stripe/source',
			data,
		});
	},

	getPaymentDisabled: () => {
		if (process.env.VUE_APP_PAYMENTS_DISABLED === 'true') {
			return true;
		} else {
			return false;
		}
	},
};

export default BillPay;
